import { AgentGatewayService } from "@/services/agent-gateway-service";
import {
  AgentStreamConnectionError,
  AgentStreamTerminalError,
} from "@/services/agent-stream-errors";
import type { AgentSession } from "@/services/types/agent-gateway";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import type { ComposerStatus } from "./AgentComposer";
import { classifyAgentGatewayFailure } from "./agent-gateway-failure";
import {
  type OutgoingMessageAttempt,
  type StreamingMessage,
  createMessageAttempt,
  mergeNormalizedEvents,
} from "./message-utils";
import { agentGatewayQueryKeys } from "./query-options";

type SendMessageVariables = {
  agentKey: string;
  sessionId: string;
  attempt: OutgoingMessageAttempt;
};

type ActiveRequest = {
  scope: string;
  idempotencyKey: string;
  controller: AbortController;
};

type InFlightSubmission = Omit<ActiveRequest, "controller">;

const STREAM_RECOVERY_DELAYS_MS = [250, 750, 1_500] as const;
const WORKFLOW_START_IDEMPOTENCY_KEY = "workflow-start-v1";
const WORKFLOW_START_LABEL = "Start lead research";

function isRecoverableStreamError(error: unknown) {
  if (error instanceof AgentStreamConnectionError) {
    return true;
  }

  return classifyAgentGatewayFailure(error).kind === "already_running";
}

function waitForStreamRecovery(delayMs: number, signal: AbortSignal) {
  return new Promise<void>((resolve, reject) => {
    if (signal.aborted) {
      reject(
        signal.reason ??
          new DOMException("The request was aborted.", "AbortError"),
      );
      return;
    }

    const timeoutId = globalThis.setTimeout(() => {
      signal.removeEventListener("abort", handleAbort);
      resolve();
    }, delayMs);

    function handleAbort() {
      globalThis.clearTimeout(timeoutId);
      reject(
        signal.reason ??
          new DOMException("The request was aborted.", "AbortError"),
      );
    }

    signal.addEventListener("abort", handleAbort, { once: true });
  });
}

export function useAgentMessageController(agentKey: string, sessionId: string) {
  const queryClient = useQueryClient();
  const activeScopeRef = useRef<string | null>(`${agentKey}:${sessionId}`);
  const activeRequestRef = useRef<ActiveRequest | null>(null);
  const submissionInFlightRef = useRef<InFlightSubmission | null>(null);
  const [draft, setDraftValue] = useState("");
  const [outgoingAttempts, setOutgoingAttempts] = useState<
    OutgoingMessageAttempt[]
  >([]);
  const [streamingMessages, setStreamingMessages] = useState<
    Record<string, StreamingMessage>
  >({});
  const [composerError, setComposerError] = useState<string | null>(null);
  const [composerStatus, setComposerStatus] = useState<ComposerStatus>("idle");
  const [announcement, setAnnouncement] = useState("");

  const messageMutation = useMutation({
    mutationFn: async ({
      agentKey: sentAgentKey,
      sessionId: sentSessionId,
      attempt,
    }: SendMessageVariables) => {
      const sentScope = `${sentAgentKey}:${sentSessionId}`;
      const controller = new AbortController();
      const seenFrameIds = new Set<string>();
      activeRequestRef.current = {
        scope: sentScope,
        idempotencyKey: attempt.idempotencyKey,
        controller,
      };
      const sessionQueryKey = agentGatewayQueryKeys.session(
        sentAgentKey,
        sentSessionId,
      );
      let recoveryAttempt = 0;

      while (true) {
        try {
          const stream =
            attempt.requestKind === "workflow_start"
              ? AgentGatewayService.streamWorkflowStart(
                  sentAgentKey,
                  sentSessionId,
                  attempt.idempotencyKey,
                  controller.signal,
                )
              : AgentGatewayService.streamMessage(
                  sentAgentKey,
                  sentSessionId,
                  attempt.text,
                  attempt.idempotencyKey,
                  controller.signal,
                );
          for await (const frame of stream) {
            const activeRequest = activeRequestRef.current;
            if (
              activeScopeRef.current !== sentScope ||
              activeRequest?.idempotencyKey !== attempt.idempotencyKey
            ) {
              continue;
            }

            if (frame.id && seenFrameIds.has(frame.id)) {
              continue;
            }
            if (frame.id) {
              seenFrameIds.add(frame.id);
            }

            switch (frame.event) {
              case "message_delta":
                setStreamingMessages((current) => {
                  const existing = current[frame.data.message_key];
                  return {
                    ...current,
                    [frame.data.message_key]: {
                      messageKey: frame.data.message_key,
                      text: `${existing?.text ?? ""}${frame.data.delta}`,
                    },
                  };
                });
                break;

              case "agent_event":
                queryClient.setQueryData<AgentSession>(
                  sessionQueryKey,
                  (current) =>
                    current
                      ? {
                          ...current,
                          events: mergeNormalizedEvents(current.events, [
                            frame.data.event,
                          ]),
                        }
                      : current,
                );

                if (frame.data.message_key) {
                  setStreamingMessages((current) => {
                    const next = { ...current };
                    delete next[frame.data.message_key as string];
                    return next;
                  });
                }
                break;

              case "complete":
                return frame.data;

              case "error":
                throw new AgentStreamTerminalError(
                  frame.data.code,
                  frame.data.message,
                  frame.data.correlation_id,
                );
            }
          }

          throw new AgentStreamConnectionError();
        } catch (error) {
          if (
            controller.signal.aborted ||
            activeScopeRef.current !== sentScope ||
            !isRecoverableStreamError(error) ||
            recoveryAttempt >= STREAM_RECOVERY_DELAYS_MS.length
          ) {
            throw error;
          }

          const delayMs = STREAM_RECOVERY_DELAYS_MS[recoveryAttempt];
          recoveryAttempt += 1;

          setAnnouncement(
            `Connection interrupted. Reconnecting ${recoveryAttempt} of ` +
              `${STREAM_RECOVERY_DELAYS_MS.length}.`,
          );
          await waitForStreamRecovery(delayMs, controller.signal);
        }
      }
    },
    onSuccess: (_response, variables) => {
      const sentScope = `${variables.agentKey}:${variables.sessionId}`;
      if (
        submissionInFlightRef.current?.scope === sentScope &&
        submissionInFlightRef.current.idempotencyKey ===
          variables.attempt.idempotencyKey
      ) {
        submissionInFlightRef.current = null;
      }
      if (
        activeRequestRef.current?.scope === sentScope &&
        activeRequestRef.current.idempotencyKey ===
          variables.attempt.idempotencyKey
      ) {
        activeRequestRef.current = null;
      }

      const sessionQueryKey = agentGatewayQueryKeys.session(
        variables.agentKey,
        variables.sessionId,
      );
      if (activeScopeRef.current === sentScope) {
        setStreamingMessages({});
        setOutgoingAttempts((current) =>
          current.filter(
            (item) => item.idempotencyKey !== variables.attempt.idempotencyKey,
          ),
        );
        setComposerError(null);
        setComposerStatus("completed");
        setAnnouncement(
          variables.attempt.requestKind === "workflow_start"
            ? "Lead research started."
            : "Message completed.",
        );
      }

      void queryClient.invalidateQueries({
        exact: true,
        queryKey: sessionQueryKey,
      });
      void queryClient.invalidateQueries({
        exact: true,
        queryKey: agentGatewayQueryKeys.sessions(variables.agentKey),
      });
    },
    onError: (error, variables) => {
      const sentScope = `${variables.agentKey}:${variables.sessionId}`;
      const failure = classifyAgentGatewayFailure(error);

      if (
        submissionInFlightRef.current?.scope === sentScope &&
        submissionInFlightRef.current.idempotencyKey ===
          variables.attempt.idempotencyKey
      ) {
        submissionInFlightRef.current = null;
      }
      if (
        activeRequestRef.current?.scope === sentScope &&
        activeRequestRef.current.idempotencyKey ===
          variables.attempt.idempotencyKey
      ) {
        activeRequestRef.current = null;
      }

      if (failure.refreshAccess) {
        void queryClient.invalidateQueries({
          queryKey: agentGatewayQueryKeys.availableAgents(),
        });
        void queryClient.invalidateQueries({
          exact: true,
          queryKey: agentGatewayQueryKeys.session(
            variables.agentKey,
            variables.sessionId,
          ),
        });
      }

      if (activeScopeRef.current !== sentScope) {
        return;
      }

      setStreamingMessages({});
      if (failure.restoreDraft) {
        setOutgoingAttempts((current) =>
          current.filter(
            (item) => item.idempotencyKey !== variables.attempt.idempotencyKey,
          ),
        );
        setDraftValue(variables.attempt.text);
        setComposerError(failure.message);
      } else {
        setOutgoingAttempts((current) =>
          current.map((item) =>
            item.idempotencyKey === variables.attempt.idempotencyKey
              ? {
                  ...item,
                  status: "failed",
                  errorMessage: failure.message,
                  failureKind: failure.kind,
                  retryable: failure.retryable,
                }
              : item,
          ),
        );
        setComposerError(null);
      }

      setComposerStatus("failed");
      setAnnouncement(
        variables.attempt.requestKind === "workflow_start"
          ? failure.retryable
            ? "Lead research failed to start. A retry is available."
            : failure.message
          : failure.retryable
            ? "Message failed to send. A retry is available."
            : failure.message,
      );
    },
  });

  useEffect(() => {
    const scope = `${agentKey}:${sessionId}`;
    activeScopeRef.current = scope;
    submissionInFlightRef.current = null;
    setDraftValue("");
    setOutgoingAttempts([]);
    setStreamingMessages({});
    setComposerError(null);
    setComposerStatus("idle");
    setAnnouncement("");

    return () => {
      if (activeScopeRef.current === scope) {
        activeScopeRef.current = null;
      }
      if (activeRequestRef.current?.scope === scope) {
        activeRequestRef.current.controller.abort();
        activeRequestRef.current = null;
      }
      if (submissionInFlightRef.current?.scope === scope) {
        submissionInFlightRef.current = null;
      }
    };
  }, [agentKey, sessionId]);

  const setDraft = (value: string) => {
    setDraftValue(value);
    setComposerError(null);
    if (composerStatus === "completed" || composerStatus === "failed") {
      setComposerStatus("idle");
      setAnnouncement("");
    }
  };

  const submitMessage = (
    text: string,
    options: {
      idempotencyKey?: string;
      requestKind?: "message" | "workflow_start";
    } = {},
  ) => {
    if (submissionInFlightRef.current) {
      return;
    }

    const sessionQueryKey = agentGatewayQueryKeys.session(agentKey, sessionId);
    const currentSession =
      queryClient.getQueryData<AgentSession>(sessionQueryKey);
    const attempt = createMessageAttempt(text, {
      afterEventId: currentSession?.events.at(-1)?.id ?? null,
      ...options,
    });
    submissionInFlightRef.current = {
      scope: `${agentKey}:${sessionId}`,
      idempotencyKey: attempt.idempotencyKey,
    };
    setDraftValue("");
    setComposerError(null);
    setComposerStatus("pending");
    setAnnouncement(
      attempt.requestKind === "workflow_start"
        ? "Starting lead research."
        : "Sending message.",
    );
    setOutgoingAttempts((current) => [...current, attempt]);
    messageMutation.mutate({ agentKey, sessionId, attempt });
  };

  const sendMessage = (text: string) => submitMessage(text);

  const startWorkflow = () =>
    submitMessage(WORKFLOW_START_LABEL, {
      idempotencyKey: WORKFLOW_START_IDEMPOTENCY_KEY,
      requestKind: "workflow_start",
    });

  const retryMessage = (attempt: OutgoingMessageAttempt) => {
    if (
      submissionInFlightRef.current ||
      attempt.status !== "failed" ||
      !attempt.retryable
    ) {
      return;
    }

    const pendingAttempt: OutgoingMessageAttempt = {
      ...attempt,
      status: "pending",
      errorMessage: undefined,
      failureKind: undefined,
      retryable: undefined,
    };

    submissionInFlightRef.current = {
      scope: `${agentKey}:${sessionId}`,
      idempotencyKey: attempt.idempotencyKey,
    };
    setComposerError(null);
    setComposerStatus("pending");
    setAnnouncement("Retrying message.");
    setOutgoingAttempts((current) =>
      current.map((item) =>
        item.idempotencyKey === attempt.idempotencyKey ? pendingAttempt : item,
      ),
    );
    messageMutation.mutate({ agentKey, sessionId, attempt: pendingAttempt });
  };

  return {
    announcement,
    composerError,
    composerStatus,
    draft,
    isPending: composerStatus === "pending",
    outgoingAttempts,
    retryMessage,
    sendMessage,
    setDraft,
    startWorkflow,
    streamingMessages: Object.values(streamingMessages),
  };
}
